import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
var _excluded = ["children", "defaultValue", "onChange", "value"];
import React from 'react';
import PropTypes from 'prop-types';
import ButtonGroup from '../ButtonGroup/ButtonGroup';
/**
 * Toggle button group is a [`ButtonGroup`](#/Button%20components?id=buttongroup) limited to
 * [`ToggleButton`](#/Button%20components?id=togglebutton)  components. Similiar to how radio
 * buttons work, the buttons in a toggle button group are
 * **mutually exclusive** -- only one button can be selected at any given time. If you want to
 * select multiple toggles buttons in a group, you can use a simple
 * [`ButtonGroup`](#/Button%20components?id=buttongroup) instead.
 *
 * @since 8.0.0
 */

var ToggleButtonGroup = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
      defaultValue = _ref.defaultValue,
      onChange = _ref.onChange,
      value = _ref.value,
      rest = _objectWithoutProperties(_ref, _excluded);

  var _React$useState = React.useState(defaultValue),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      valueState = _React$useState2[0],
      setValueState = _React$useState2[1];

  var controlled = typeof value !== 'undefined';
  var pressedValue = controlled ? value : valueState;
  var onClickCallback = React.useCallback(function (e, pressed) {
    // Get the next expected value
    var nextValue = pressedValue;

    if (!pressed) {
      nextValue = e.currentTarget.value;
    } // Only update if values differ


    if (nextValue !== pressedValue) {
      setValueState(nextValue);

      if (onChange) {
        onChange(nextValue);
      }
    }
  }, [pressedValue, onChange]);
  return /*#__PURE__*/React.createElement(ButtonGroup, _extends({
    ref: ref
  }, rest), React.Children.map(children, function (child) {
    return child && /*#__PURE__*/React.cloneElement(child, {
      pressed: child.props.value === pressedValue,
      onClick: onClickCallback
    });
  }));
});
ToggleButtonGroup.displayName = 'ToggleButtonGroup';
ToggleButtonGroup.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * A group of [`ToggleButton`](#/Button%20components?id=togglebutton) components.
   */
  children: PropTypes.node.isRequired,

  /**
   * The [`ButtonGroup`](#/Button%20components?id=buttongroup) `conjoined`
   * prop, set to `true` by default for toggle button groups.
   */
  conjoined: PropTypes.bool,

  /**
   * Set the toggle button group value, and use as an
   * [uncontrolled component](https://reactjs.org/docs/uncontrolled-components.html).
   *
   * A toggle button group can set `value` or `defaultValue`, but not both.
   */
  defaultValue: PropTypes.string,

  /**
   * Callback for change events within the toggle button group. The callback will receive the
   * `value` of the currently selected
   * [`ToggleButton`](#/Button%20components?id=togglebutton), or `undefined` if all
   * buttons are unpressed.
   */
  onChange: PropTypes.func,

  /**
   * Set the toggle button group value, and use as an
   * [controlled component](https://reactjs.org/docs/forms.html#controlled-components).
   *
   * A toggle button group can set `value` or `defaultValue`, but not both.
   */
  value: PropTypes.string
} : {};
ToggleButtonGroup.defaultProps = {
  conjoined: true,
  defaultValue: undefined,
  onChange: undefined,
  value: undefined
};
export default ToggleButtonGroup;